import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { GitMerge, ShieldAlert, ShieldCheck, ChevronDown, ChevronUp, Check, Star, FileText, MessageSquare, Share2, Tag, Loader2 } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { statusColor } from './constants';
import logger from '@/lib/logger';

function ClusterCard({ cluster, expanded, onToggle, selectedPrimary, onSelectPrimary, merged }) {
  const conf = cluster.confidence;
  return (
    <div className={`border rounded-sm transition-all ${merged ? 'border-emerald-200 bg-emerald-50/30' : 'border-slate-200 bg-white'}`} data-testid={`cluster-${cluster.id}`}>
      <button onClick={onToggle} className="w-full flex items-center justify-between p-3 text-left hover:bg-slate-50/50 transition-colors">
        <div className="flex items-center gap-2.5 min-w-0">
          {merged ? (
            <div className="w-7 h-7 rounded-sm bg-emerald-100 flex items-center justify-center shrink-0"><Check className="w-4 h-4 text-emerald-600" /></div>
          ) : conf === 'high' ? (
            <div className="w-7 h-7 rounded-sm bg-red-50 flex items-center justify-center shrink-0"><ShieldAlert className="w-4 h-4 text-red-500" /></div>
          ) : (
            <div className="w-7 h-7 rounded-sm bg-amber-50 flex items-center justify-center shrink-0"><ShieldCheck className="w-4 h-4 text-amber-500" /></div>
          )}
          <div className="min-w-0">
            <p className="text-sm font-medium text-slate-900 truncate">
              {cluster.leads.map(l => l.name).join(', ')}
            </p>
            <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
              {cluster.reasons.map(r => (
                <span key={r} className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-sm ${conf === 'high' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{r}</span>
              ))}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded-sm">{cluster.count} leads</span>
          {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </div>
      </button>

      {expanded && !merged && (
        <div className="border-t border-slate-100 p-3 space-y-2" data-testid={`cluster-expanded-${cluster.id}`}>
          <p className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-medium mb-1">Select primary lead to keep (others merge into it):</p>
          {cluster.leads.map(lead => {
            const isPrimary = selectedPrimary === lead.id;
            return (
              <button
                key={lead.id}
                onClick={() => onSelectPrimary(lead.id)}
                className={`w-full text-left p-2.5 rounded-sm border transition-all ${
                  isPrimary ? 'border-[#0047AB] bg-blue-50 shadow-sm' : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                }`}
                data-testid={`select-primary-${lead.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    {isPrimary && <Star className="w-3.5 h-3.5 text-[#0047AB] shrink-0" />}
                    <div className="min-w-0">
                      <p className={`text-sm truncate ${isPrimary ? 'font-semibold text-[#0047AB]' : 'font-medium text-slate-900'}`}>{lead.name}</p>
                      <p className="text-[10px] text-slate-500 truncate">
                        {lead.company && `${lead.company} · `}{lead.email || lead.phone || '—'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${statusColor(lead.status)}`}>{lead.status}</span>
                    <div className="flex items-center gap-1 text-[10px] text-slate-400">
                      {lead.notes_count > 0 && <span className="flex items-center gap-0.5"><MessageSquare className="w-3 h-3" />{lead.notes_count}</span>}
                      {lead.docs_count > 0 && <span className="flex items-center gap-0.5"><FileText className="w-3 h-3" />{lead.docs_count}</span>}
                      {lead.shared_count > 0 && <span className="flex items-center gap-0.5"><Share2 className="w-3 h-3" />{lead.shared_count}</span>}
                      {lead.tags_count > 0 && <span className="flex items-center gap-0.5"><Tag className="w-3 h-3" />{lead.tags_count}</span>}
                    </div>
                    {isPrimary && <span className="text-[9px] font-semibold text-[#0047AB] bg-blue-100 px-1.5 py-0.5 rounded-sm">PRIMARY</span>}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
      {expanded && merged && (
        <div className="border-t border-emerald-100 p-3 text-center">
          <p className="text-xs text-emerald-600 font-medium flex items-center justify-center gap-1"><Check className="w-3.5 h-3.5" /> Merged successfully</p>
        </div>
      )}
    </div>
  );
}

export function BulkMergeDialog({ open, onClose, clusters, scanning, onRefresh }) {
  const [expanded, setExpanded] = useState({});
  const [primaryMap, setPrimaryMap] = useState({});
  const [mergedSet, setMergedSet] = useState(new Set());
  const [merging, setMerging] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  const toggleExpand = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  const selectPrimary = (clusterId, leadId) => setPrimaryMap(prev => ({ ...prev, [clusterId]: leadId }));

  const mergeSingleCluster = async (cluster) => {
    const primaryId = primaryMap[cluster.id];
    if (!primaryId) { toast.error('Select a primary lead first'); return; }
    const secondaryIds = cluster.leads.filter(l => l.id !== primaryId).map(l => l.id);
    try {
      for (const secId of secondaryIds) {
        await api.mergeLeads({ primary_id: primaryId, secondary_id: secId, field_overrides: {} });
      }
      setMergedSet(prev => new Set([...prev, cluster.id]));
      toast.success(`Merged ${secondaryIds.length} lead${secondaryIds.length > 1 ? 's' : ''} into primary`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Merge failed');
    }
  };

  const mergeAll = async () => {
    const pending = clusters.filter(c => !mergedSet.has(c.id));
    const ready = pending.filter(c => primaryMap[c.id]);
    if (ready.length === 0) { toast.error('Select a primary lead for at least one cluster'); return; }
    setMerging(true);
    setProgress({ done: 0, total: ready.length });
    let done = 0;
    for (const cluster of ready) {
      const primaryId = primaryMap[cluster.id];
      const secondaryIds = cluster.leads.filter(l => l.id !== primaryId).map(l => l.id);
      try {
        for (const secId of secondaryIds) {
          await api.mergeLeads({ primary_id: primaryId, secondary_id: secId, field_overrides: {} });
        }
        setMergedSet(prev => new Set([...prev, cluster.id]));
      } catch (e) {
        logger.error(`Failed to merge cluster ${cluster.id}:`, e);
      }
      done++;
      setProgress({ done, total: ready.length });
    }
    setMerging(false);
    toast.success(`Bulk merge complete: ${done} cluster${done > 1 ? 's' : ''} merged`);
    if (onRefresh) onRefresh();
  };

  const handleClose = () => {
    if (mergedSet.size > 0 && onRefresh) onRefresh();
    setExpanded({});
    setPrimaryMap({});
    setMergedSet(new Set());
    setProgress({ done: 0, total: 0 });
    onClose();
  };

  const pendingCount = clusters.filter(c => !mergedSet.has(c.id)).length;
  const readyCount = clusters.filter(c => !mergedSet.has(c.id) && primaryMap[c.id]).length;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl rounded-sm max-h-[90vh] flex flex-col" data-testid="bulk-merge-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <GitMerge className="w-5 h-5 text-[#0047AB]" /> Bulk Duplicate Scan
          </DialogTitle>
        </DialogHeader>

        {scanning ? (
          <div className="flex flex-col items-center justify-center py-16">
            <Loader2 className="w-8 h-8 text-[#0047AB] animate-spin mb-3" />
            <p className="text-sm font-medium text-slate-700">Scanning all leads for duplicates...</p>
            <p className="text-xs text-slate-400 mt-1">This may take a moment</p>
          </div>
        ) : clusters.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16">
            <Check className="w-10 h-10 text-emerald-400 mb-3" />
            <p className="text-sm font-semibold text-slate-900">No duplicates found</p>
            <p className="text-xs text-slate-400 mt-1">Your lead database is clean!</p>
          </div>
        ) : (
          <>
            {/* Stats bar */}
            <div className="flex items-center justify-between py-2 px-1 border-b border-slate-100 -mt-2 mb-2">
              <div className="flex items-center gap-3 text-xs text-slate-500">
                <span><strong className="text-slate-900">{clusters.length}</strong> duplicate cluster{clusters.length > 1 ? 's' : ''}</span>
                <span><strong className="text-slate-900">{clusters.reduce((s, c) => s + c.count, 0)}</strong> total leads involved</span>
                {mergedSet.size > 0 && <span className="text-emerald-600"><strong>{mergedSet.size}</strong> merged</span>}
              </div>
              {merging && (
                <div className="flex items-center gap-2 text-xs text-[#0047AB]">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  <span>{progress.done}/{progress.total} clusters</span>
                </div>
              )}
            </div>

            {/* Clusters list */}
            <div className="flex-1 overflow-y-auto space-y-2 min-h-0 max-h-[55vh] pr-1">
              {clusters.map(cluster => (
                <ClusterCard
                  key={cluster.id}
                  cluster={cluster}
                  expanded={!!expanded[cluster.id]}
                  onToggle={() => toggleExpand(cluster.id)}
                  selectedPrimary={primaryMap[cluster.id]}
                  onSelectPrimary={(leadId) => selectPrimary(cluster.id, leadId)}
                  merged={mergedSet.has(cluster.id)}
                />
              ))}
            </div>
          </>
        )}

        <DialogFooter className="border-t border-slate-100 pt-3 mt-2">
          <Button variant="outline" onClick={handleClose} className="rounded-sm" data-testid="bulk-merge-close-btn">
            {mergedSet.size > 0 ? 'Done' : 'Close'}
          </Button>
          {clusters.length > 0 && pendingCount > 0 && (
            <Button
              onClick={mergeAll}
              disabled={merging || readyCount === 0}
              className="bg-[#0047AB] hover:bg-[#003580] text-white rounded-sm"
              data-testid="merge-all-btn"
            >
              {merging ? (
                <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> Merging {progress.done}/{progress.total}...</>
              ) : (
                <><GitMerge className="w-3.5 h-3.5 mr-1.5" /> Merge All ({readyCount} ready)</>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
